import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import axiosInstance from '../../../utils/axios';

const PRINT_CONTENT_CLASS = 'modal-print-content-tax-invoice';
const PRINT_CSS_PATH = '/css/tax_invoice_print.css';

const TaxInvoicePrintModal = ({ open, onClose, title = 'Tax Invoice Print', children, documentType, documentId }) => {
    const [isPrinting, setIsPrinting] = useState(false);

    const handlePrint = useCallback(async () => {
        if (isPrinting) return;

        if (!documentType || !documentId) {
            toast.error('Document ID is missing.');
            return;
        }

        setIsPrinting(true);
        try {
            const response = await axiosInstance.get(
                `/tax_invoice_print/${encodeURIComponent(documentType)}/${encodeURIComponent(documentId)}`,
                {
                    responseType: 'blob',
                    headers: { Accept: 'application/pdf' },
                }
            );

            const contentType = response.headers['content-type'] || '';
            if (!contentType.includes('pdf')) {
                const text = await response.data.text();
                let message = 'Failed to generate PDF.';
                try {
                    const parsed = JSON.parse(text);
                    message = parsed.error || parsed.message || message;
                } catch {
                    if (text) message = text;
                }
                toast.error(message);
                return;
            }

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const blobUrl = URL.createObjectURL(blob);
            const opened = window.open(blobUrl, '_blank');
            if (!opened) {
                toast.error('Popup blocked. Please allow popups for this site.');
            }

            setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        } catch (error) {
            let message = 'Failed to generate PDF.';
            if (error.response?.data instanceof Blob) {
                try {
                    const text = await error.response.data.text();
                    const parsed = JSON.parse(text);
                    message = parsed.error || parsed.message || message;
                } catch {
                    // keep default message
                }
            } else {
                message = error.response?.data?.error || error.response?.data?.message || error.message || message;
            }
            toast.error(message);
            console.error('Failed to load PDF:', error);
        } finally {
            setIsPrinting(false);
        }
    }, [documentType, documentId, isPrinting]);

    useEffect(() => {
        if (!open) {
            setIsPrinting(false);
            return undefined;
        }

        const cssHref = `${process.env.PUBLIC_URL || ''}${PRINT_CSS_PATH}`;
        const existing = document.getElementById('tax-invoice-preview-css');
        if (existing) existing.remove();

        const link = document.createElement('link');
        link.id = 'tax-invoice-preview-css';
        link.rel = 'stylesheet';
        link.href = cssHref;
        document.head.appendChild(link);

        return () => {
            document.getElementById('tax-invoice-preview-css')?.remove();
        };
    }, [open]);

    if (!open) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
        }}>
            <div style={{
                backgroundColor: 'white',
                width: '90%',
                height: '90%',
                borderRadius: '8px',
                position: 'relative',
                overflow: 'hidden'
            }}>
                <div style={{
                    padding: '15px',
                    borderBottom: '1px solid #ddd',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <h5 style={{ margin: 0 }}>{title || 'Print Preview'}</h5>
                    <div>
                        <button
                            type="button"
                            className="btn btn-primary btn-sm me-2"
                            onClick={handlePrint}
                            disabled={isPrinting}
                        >
                            {isPrinting ? (
                                <><i className="fas fa-spinner fa-spin me-1"></i> Generating...</>
                            ) : (
                                <><i className="fas fa-print me-1"></i> Print</>
                            )}
                        </button>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
                            <i className="fas fa-times me-1"></i>
                            Close
                        </button>
                    </div>
                </div>
                <div
                    style={{
                        height: 'calc(100% - 70px)',
                        overflow: 'auto',
                        padding: '20px',
                        fontFamily: 'sans-serif',
                    }}
                    className={PRINT_CONTENT_CLASS}
                >
                    <style>{`
                        .${PRINT_CONTENT_CLASS} #container {
                            width: 200mm;
                            max-width: 200mm;
                            margin: 0 auto;
                            padding: 0;
                        }
                        .${PRINT_CONTENT_CLASS} #client-info .client-address {
                            display: block !important;
                        }
                        .${PRINT_CONTENT_CLASS} #client-contacts > div,
                        .${PRINT_CONTENT_CLASS} #client-info > div {
                            justify-content: flex-start !important;
                        }
                        .${PRINT_CONTENT_CLASS} .page-indicator {
                            display: none;
                        }
                        @media print {
                            .${PRINT_CONTENT_CLASS} .page-indicator {
                                display: block;
                                position: fixed;
                                bottom: 0;
                                left: 0;
                                text-align: left;
                                font-size: 9px;
                                font-weight: bold !important;
                                text-transform: uppercase;
                                padding-left: 18px;
                                padding-bottom: 18px;
                            }
                            .${PRINT_CONTENT_CLASS} .page-indicator::before {
                                content: "PAGE " counter(page) " OF " counter(pages);
                            }
                        }
                    `}</style>
                    {children}
                </div>
            </div>
        </div>
    );
};

export default TaxInvoicePrintModal;
